/**
 * The admin app: who is signed in decides what renders. No session shows sign-in. A session
 * without a staff role shows why there is nothing here. Everyone else gets the rail and the
 * screen the URL names. The engine role stays on de-identified screens.
 *
 * The Worker enforces every one of these rules on its own. This screen only avoids showing a
 * staff member a page whose every request would be refused.
 */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { Me } from "./api.ts";
import { api } from "./api.ts";
import { Shell } from "./chrome.tsx";
import { useLoad } from "./load.ts";
import { Audit } from "./Audit.tsx";
import { Catalog } from "./Catalog.tsx";
import { Evaluation } from "./Evaluation.tsx";
import { Jobs } from "./Jobs.tsx";
import { OrganizationDetail } from "./OrganizationDetail.tsx";
import { PatientRecord } from "./PatientRecord.tsx";
import { PharmacyOrder } from "./PharmacyOrder.tsx";
import { PharmacyOrders } from "./PharmacyOrders.tsx";
import { Reactions } from "./Reactions.tsx";
import { Rules } from "./Rules.tsx";
import { ReviewQueue } from "./ReviewQueue.tsx";
import { ReviewScreen } from "./ReviewScreen.tsx";
import { navigate, useScreen } from "./router.ts";
import type { Screen } from "./router.ts";
import { Funnel } from "./Funnel.tsx";
import { PageHead, Panel } from "./layout.tsx";
import { Search } from "./Search.tsx";
import { Card, CardCopy, SignIn } from "./SignIn.tsx";
import { Button } from "./ui.tsx";
import { UserDetail } from "./UserDetail.tsx";
import { refusal } from "./words.ts";
function EngineHome() {
  return <>
      <PageHead title="Formula engine" lead="Catalog, rules and evaluation. No names or records." permit="You can edit the engine. You cannot open a patient record." />
      <Panel padded>
        Catalog, rules and evaluation are de-identified. They never show a name or a record.
      </Panel>
    </>;
}
function namedScreen(kind: Screen["kind"]): boolean {
  switch (kind) {
    case "catalog":
    case "rules":
    case "evaluation":
    case "missing":
      return false;
    default:
      return true;
  }
}
function Hidden({
  title,
  lead
}: {
  readonly title: string;
  readonly lead: string;
}) {
  return <>
      <PageHead title={title} lead={lead} />
      <Panel padded>{lead}</Panel>
    </>;
}
function hiddenCopy(me: Me, screen: Screen): {
  readonly title: string;
  readonly lead: string;
} | null {
  if (me.staffRole === "engine" && screen.kind !== "funnel" && screen.kind !== "search" && namedScreen(screen.kind)) return {
    title: "Nothing here for the engine role",
    lead: "Engine staff work on de-identified cases. This screen names people."
  };
  if (me.staffRole === "clinician" && (screen.kind === "orders" || screen.kind === "pharmacyOrder")) return {
    title: "Orders",
    lead: "Orders are not on a clinician's desk. Fulfillment is operations."
  };
  return null;
}
function Body({
  screen,
  me
}: {
  readonly screen: Screen;
  readonly me: Me;
}) {
  const engine = me.staffRole === "engine" || me.staffRole === "superuser";
  const hidden = hiddenCopy(me, screen);
  if (hidden !== null) return <Hidden title={hidden.title} lead={hidden.lead} />;
  switch (screen.kind) {
    case "funnel":
      return me.staffRole === "engine" ? <EngineHome /> : <Funnel stage={screen.stage} />;
    case "search":
      return me.staffRole === "engine" ? <EngineHome /> : <Search query={screen.query} view={screen.view} />;
    case "catalog":
      return engine ? <Catalog ingredient={screen.ingredient} creating={screen.creating} /> : <Hidden title="Engine staff only" lead="The catalog is for the engine role." />;
    case "rules":
      return engine ? <Rules view={screen.view} /> : <Hidden title="Engine staff only" lead="Rule versions are for the engine role." />;
    case "evaluation":
      return engine ? <Evaluation /> : <Hidden title="Engine staff only" lead="Evaluation is for the engine role." />;
    case "jobs":
      return <Jobs me={me} view={screen.view} />;
    case "audit":
      return <Audit actor={screen.actor} patient={screen.patient} from={screen.from} to={screen.to} />;
    case "user":
      return <UserDetail id={screen.id} me={me} />;
    case "organization":
      return <OrganizationDetail id={screen.id} me={me} />;
    case "patient":
      return <PatientRecord id={screen.id} />;
    case "reviews":
      return <ReviewQueue />;
    case "review":
      return <ReviewScreen id={screen.id} />;
    case "orders":
      return <PharmacyOrders state={screen.state} />;
    case "pharmacyOrder":
      return <PharmacyOrder id={screen.id} />;
    case "reactions":
      return <Reactions />;
    case "missing":
      return <Hidden title="No screen here" lead="Check the address, or search from the rail." />;
  }
}
function Staff({
  me,
  onSignOut
}: {
  readonly me: Me;
  readonly onSignOut: () => void;
}) {
  const screen = useScreen();
  return <Shell me={me} onSignOut={onSignOut}>
      <Body screen={screen} me={me} />
    </Shell>;
}
function NoAccess({
  me,
  onSignOut
}: {
  readonly me: Me;
  readonly onSignOut: () => void;
}) {
  return <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda xl4dl4e xbz0q3o">
      <Card>
        {me.staffRole === "engine" ? <CardCopy title="Nothing here for the engine role">
            Engine staff work on de-identified cases, and every screen here shows identified
            accounts and records.
          </CardCopy> : <CardCopy title="No staff role">
            {me.email ?? "This account"} is signed in but has no staff role. A superuser can grant
            one.
          </CardCopy>}
        <Button tone="quiet" onClick={onSignOut}>
          Sign out
        </Button>
      </Card>
    </div>;
}
export function App() {
  const [session, setSession] = useState(0);
  const [me] = useLoad(`me-${session}`, () => api<Me>(ROUTES.me));
  async function signOut() {
    await api({
      ...ROUTES.authPost,
      path: "/api/auth/sign-out"
    }, {
      body: {}
    });
    navigate("/", "replace");
    setSession(n => n + 1);
  }
  if (me.state === "loading") return <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda xl4dl4e xbz0q3o" />;
  if (me.state === "failed") return <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda xl4dl4e xbz0q3o">
        {me.status === 401 ? <SignIn /> : <Card>
            <CardCopy title="Cannot load the admin app">{refusal(me.error)}</CardCopy>
          </Card>}
      </div>;
  if (me.data.staffRole === null) return <NoAccess me={me.data} onSignOut={() => void signOut()} />;
  return <Staff me={me.data} onSignOut={() => void signOut()} />;
}